// Stage moves — the pure half (no React, no DOM) so `node --test` can load
// this exact file (test/stage_moves_logic.test.mjs). views/stage_moves.jsx
// (the timeline hover card + the Undo toast) reads these off
// window.StageMovesLogic.
//
// stage_history only holds Atlas-UI moves (models.py, migration 103 note):
// a stage the deal reached in DealCloud has no row, so its card falls back
// to the DC stage dates on the engagement and says "Before Atlas".

const DAY_MS = 86400000;

const ts = (v) => {
  if (!v) return null;
  const t = new Date(String(v).length === 10 ? `${v}T00:00:00` : v).getTime();
  return Number.isNaN(t) ? null : t;
};
const daysBetween = (a, b) => (a == null || b == null ? null : Math.max(0, Math.floor((b - a) / DAY_MS)));

// The DC stage-passage date that says a deal reached a stage (migration 103).
// Only two are populated in practice; lost_pre_mandate_date is dead (KNOWLEDGE.md).
const DC_STAGE_DATE = { loi: 'loi_date', won: 'closed_stage_date', ss_won: 'closed_stage_date' };

const sortRows = (history) => (history || [])
  .filter((r) => r && ts(r.created_at) != null)
  .slice()
  .sort((a, b) => ts(a.created_at) - ts(b.created_at));

const actor = (r) => (r ? { at: r.created_at, by: r.changed_by_name || null } : null);

// A move and its Undo cancel out: the stay reads as if the move never
// happened (both rows stay in stage_history for the audit). Undo only ever
// reverses the deal's latest move, so pairs nest like a stack.
const UNDO_REASON = 'Undo';
const reverses = (row, prev) => Boolean(prev)
  && row.from_stage_slug === prev.to_stage_slug
  && row.to_stage_slug === prev.from_stage_slug
  && (row.from_stage_slug !== row.to_stage_slug || (Boolean(prev.exit_id) && !row.exit_id));
const cancelUndos = (rows) => {
  const out = [];
  rows.forEach((r) => {
    if (r.change_reason === UNDO_REASON && reverses(r, out[out.length - 1])) out.pop();
    else out.push(r);
  });
  return out;
};

// One stage chip's hover facts. `stages` is the rail (one family, in order),
// `raw` the engagement payload. Returns null when there is nothing to say
// (a stage the deal never reached).
//   { source: 'atlas' | 'before_atlas' | 'dealcloud' | 'skipped', entered,
//     left, exit, reason, days, current, visits, since }
// Only the current stage counts to today; any other stay ends where the deal
// next shows up somewhere else, or has no length when nothing says when.
const stageVisit = ({ history, stages, slug, raw, now = Date.now() }) => {
  const rows = cancelUndos(sortRows(history));
  const order = (stages || []).map((s) => s.slug);
  const idx = order.indexOf(slug);
  const curSlug = raw ? raw.stage_slug : null;
  const curIdx = order.indexOf(curSlug);
  const current = slug === curSlug;
  const passed = idx >= 0 && curIdx >= 0 && idx < curIdx;
  const last = rows.length ? rows[rows.length - 1] : null;

  // Both syncs are on: a deal Atlas last moved can since have moved in
  // DealCloud, to a stage no Atlas row names. It reached its current stage
  // on DC's date for that stage, or last_stage_update (the reconcile copies
  // DC's stage date) when that is newer than Atlas's last move.
  const dcMoved = Boolean(last && curSlug && last.to_stage_slug !== curSlug);
  const lsu = raw ? ts(raw.last_stage_update) : null;
  const curSince = (!last || dcMoved) && raw
    ? ((DC_STAGE_DATE[curSlug] && raw[DC_STAGE_DATE[curSlug]])
      || (lsu != null && (!last || lsu > ts(last.created_at)) ? raw.last_stage_update : null))
    : null;
  // When the deal next entered another stage outside Atlas, after `start`.
  const outsideEntries = order
    .filter((s) => s !== slug)
    .map((s) => ts((s === curSlug && curSince) || (raw && DC_STAGE_DATE[s] ? raw[DC_STAGE_DATE[s]] : null)))
    .filter((t) => t != null);
  const nextOutside = (start) => {
    const later = outsideEntries.filter((t) => start == null || t >= start);
    return later.length ? Math.min(...later) : null;
  };

  // A real move INTO the stage (an exit or a reopen writes from == to).
  const entries = rows.filter((r) => r.to_stage_slug === slug && r.from_stage_slug !== slug);

  if (current && dcMoved) {
    return {
      source: 'dealcloud', current, visits: entries.length,
      entered: null, since: curSince, reason: null, left: null, exit: null,
      days: curSince ? daysBetween(ts(curSince), now) : null,
    };
  }

  const entry = entries.length ? entries[entries.length - 1] : null;
  const after = entry ? rows.slice(rows.indexOf(entry) + 1) : rows;
  const leftRow = after.find((r) => r.from_stage_slug === slug && r.to_stage_slug !== slug) || null;
  const stay = leftRow ? after.slice(0, after.indexOf(leftRow)) : after;
  // Exited where it stood: the last exit row while at this stage (a later
  // reopen row clears it).
  const lastExitish = [...stay].reverse().find((r) => r.from_stage_slug === slug && r.to_stage_slug === slug);
  const exitRow = lastExitish && lastExitish.exit_id ? lastExitish : null;
  const exit = exitRow ? { name: exitRow.exit_name || 'Exited', reason: exitRow.change_reason || null, ...actor(exitRow) } : null;
  const endFrom = (start) => (leftRow ? ts(leftRow.created_at)
    : exitRow ? ts(exitRow.created_at)
    : current ? now
    : nextOutside(start));

  if (entry) {
    const start = ts(entry.created_at);
    return {
      source: 'atlas', current, visits: entries.length,
      entered: actor(entry), reason: entry.change_reason || null,
      left: actor(leftRow), exit,
      days: daysBetween(start, endFrom(start)),
    };
  }

  const dcField = DC_STAGE_DATE[slug];
  const dcSince = (dcField && raw && raw[dcField]) || null;
  const visitFrom = (source, since) => {
    const start = since ? ts(since) : null;
    return {
      source, current, visits: 0,
      entered: null, since, reason: null,
      left: actor(leftRow), exit,
      days: start != null ? daysBetween(start, endFrom(start)) : null,
    };
  };

  // Passed in DealCloud after Atlas's last move (between that stage and the
  // current one): DC never says whether it was skipped.
  const lastIdx = last ? order.indexOf(last.to_stage_slug) : -1;
  if (dcMoved && passed && lastIdx >= 0 && idx > lastIdx) return visitFrom('dealcloud', dcSince);

  // No Atlas move into it. Did the deal sit here before Atlas saw it?
  // Atlas's first row names the stage it found the deal at (from_stage_slug);
  // a stage earlier in the rail was passed in DealCloud, a later one that has
  // no row was jumped over. A deal Atlas created (first from is null) has no
  // DealCloud era at all.
  const first = rows[0] || null;
  const firstFrom = first ? first.from_stage_slug : undefined;
  const firstIdx = firstFrom ? order.indexOf(firstFrom) : -1;
  const sawHere = Boolean(leftRow) || (current && !rows.some((r) => r.to_stage_slug === slug));
  let before;
  if (!first) before = current || passed;
  else if (firstFrom === null) before = false;
  else if (firstFrom === slug) before = true;
  else before = passed && (firstIdx < 0 || idx < firstIdx);
  if (!before && !sawHere) {
    return passed ? { source: 'skipped', current: false, visits: 0, entered: null, left: null, exit: null, reason: null, days: null } : null;
  }

  // Entry date: DC's stage date; the current stage with no Atlas row at all
  // falls back to last_stage_update (curSince).
  return visitFrom('before_atlas', (current && curSince) || dcSince);
};

// ── Undo ────────────────────────────────────────────────────────────────
// What a stage change can put back: stage, exit, hold. Snapshot the row
// before the PATCH; undoPatch() diffs it against the response.
const stageSnapshot = (raw) => ({
  stage_id: raw ? raw.stage_id || null : null,
  stage_slug: raw ? raw.stage_slug || null : null,
  stage_name: raw ? raw.stage_name || null : null,
  exit_id: raw ? raw.exit_id || null : null,
  exit_slug: raw ? raw.exit_slug || null : null,
  exit_name: raw ? raw.exit_name || null : null,
  on_hold: raw ? raw.on_hold || null : null,
  on_hold_reason: raw ? raw.on_hold_reason || null : null,
  hold_checkin_cadence: raw ? raw.hold_checkin_cadence || null : null,
  on_hold_at: raw ? raw.on_hold_at || null : null,
  last_stage_update: raw ? raw.last_stage_update || null : null,
});

// The PATCH that returns `after` to `before`, through the normal update
// path (stage_history records it, reason "Undo"). Its `undo` block names the
// state the move left (the server refuses once the deal has moved on) and
// what the move overwrote (stage date, hold start), put back as it was.
// null = nothing to undo or not undoable here:
//   - a Company sold exit is final (the API refuses; "Not acquired" undoes it);
//   - re-exiting a reopened deal would file its exit note again — reopen
//     already asks first.
const undoPatch = (before, after) => {
  if (!before || !after) return null;
  const patch = {};
  const stageMoved = (before.stage_id || null) !== (after.stage_id || null);
  const exitMoved = (before.exit_id || null) !== (after.exit_id || null);
  const holdMoved = (before.on_hold || null) !== (after.on_hold || null);
  const undo = { stage_id: after.stage_id || null, exit_id: after.exit_id || null, on_hold: after.on_hold || null };
  if (exitMoved) {
    if (before.exit_id) return null;
    if (after.exit_slug === 'company_sold') return null;
    patch.exit_id = null;
    patch.exit_notes = null;
  }
  if (stageMoved && before.stage_id) patch.stage_id = before.stage_id;
  if (holdMoved) {
    patch.on_hold = before.on_hold || null;
    patch.on_hold_reason = before.on_hold_reason || null;
    patch.hold_checkin_cadence = before.hold_checkin_cadence || null;
    if (before.on_hold && before.on_hold_at) undo.on_hold_at = before.on_hold_at;
  }
  if (!Object.keys(patch).length) return null;
  if ('stage_id' in patch || 'exit_id' in patch) undo.last_stage_update = before.last_stage_update || null;
  patch.undo = undo;
  return patch;
};

// The toast body when an Undo is refused.
const undoErrorText = (err) => {
  const m = String((err && err.message) || err || '');
  if (m.includes('undo_stale')) return 'This deal has changed since. Nothing was undone.';
  if (m.includes('closing_required')) return 'Won needs its closing. Pick Won on the stage timeline to record it.';
  if (m.includes('company_sold_final')) return 'Company sold is final. Use “Not acquired” on the company.';
  return m || 'Try again.';
};

const holdText = (h) => (h ? `On hold · ${h}` : null);

// Toast title for what just happened: "Moved to Hot Lead", "On hold · high",
// "Off hold", "Exited · Not a fit".
const moveTitle = (before, after) => {
  if (!before || !after) return 'Saved';
  if ((before.exit_id || null) !== (after.exit_id || null)) {
    return after.exit_id ? `Exited${after.exit_name ? ` · ${after.exit_name}` : ''}` : 'Reopened';
  }
  if ((before.stage_id || null) !== (after.stage_id || null)) return `Moved to ${after.stage_name || 'new stage'}`;
  if ((before.on_hold || null) !== (after.on_hold || null)) return after.on_hold ? holdText(after.on_hold) : 'Off hold';
  return 'Saved';
};

// Toast title once Undo lands: what the deal is back at.
const restoredTitle = (before) => {
  if (!before) return 'Undone';
  const at = before.stage_name ? `Back to ${before.stage_name}` : 'Undone';
  return before.on_hold ? `${at} · ${holdText(before.on_hold)}` : at;
};

window.StageMovesLogic = { stageVisit, cancelUndos, stageSnapshot, undoPatch, undoErrorText, moveTitle, restoredTitle, DC_STAGE_DATE };
